'use client'

import { forwardRef, ReactNode, useState, useRef, useEffect } from 'react'
import { Loader2, Undo2, AlertTriangle, Check } from 'lucide-react'
import { safeAccess } from '../utils/security'

/**
 * BIRHAUS Button Component
 * 
 * Enhanced button implementing all 10 BIRHAUS principles:
 * - Cognitive load reduction through clear visual hierarchy
 * - Spanish-first labeling with optional English
 * - Undo integration for destructive actions
 * - Loading states with progress indication
 * - Accessibility compliance (WCAG AA+)
 * - Performance optimized with minimal re-renders
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export interface BirhausButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  // BIRHAUS Principle #7: Bilingual by design
  label?: string
  labelEs?: string
  labelEn?: string
  
  // BIRHAUS Principle #1: Form serves flow
  variant?: 'primary' | 'secondary' | 'destructive' | 'outline' | 'ghost'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  
  // BIRHAUS Principle #3: One clear action
  isPrimaryAction?: boolean
  
  // BIRHAUS Principle #8: Performance is UX
  loading?: boolean
  loadingText?: string
  loadingTextEs?: string
  loadingTextEn?: string
  
  // BIRHAUS Principle #5: Undo over confirm
  destructive?: boolean
  undoAction?: () => Promise<void>
  undoTimeoutMs?: number
  
  // BIRHAUS Principle #4: Progressive disclosure
  showSuccessState?: boolean
  successText?: string
  successTextEs?: string
  successTextEn?: string
  
  // BIRHAUS Principle #6: Accessibility = dignity
  describedBy?: string
  describedByEs?: string
  describedByEn?: string
  
  // Enhanced functionality
  icon?: ReactNode
  iconPosition?: 'left' | 'right'
  fullWidth?: boolean
  
  // Cognitive load tracking
  trackCognitiveLoad?: boolean
  cognitiveLoadLevel?: 'low' | 'medium' | 'high'
  
  children?: ReactNode
}

export const BirhausButton = forwardRef<HTMLButtonElement, BirhausButtonProps>(({
  // Labels (Spanish-first)
  label,
  labelEs,
  labelEn,
  
  // Appearance
  variant = 'secondary',
  size = 'md',
  isPrimaryAction = false,
  
  // States
  loading = false,
  loadingText,
  loadingTextEs,
  loadingTextEn,
  
  // Undo functionality
  destructive = false,
  undoAction,
  undoTimeoutMs = 10000,
  
  // Success state
  showSuccessState = false,
  successText,
  successTextEs,
  successTextEn,
  
  // Accessibility
  describedBy,
  describedByEs,
  describedByEn,
  
  // Enhanced props
  icon,
  iconPosition = 'left',
  fullWidth = false,
  
  // Cognitive load
  trackCognitiveLoad = false,
  cognitiveLoadLevel = 'medium',
  
  // Standard props
  className,
  disabled,
  onClick,
  children,
  ...props
}, ref) => {
  
  // BIRHAUS: State management for undo functionality
  const [showUndoPrompt, setShowUndoPrompt] = useState(false)
  const [isUndoing, setIsUndoing] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const undoTimeoutRef = useRef<NodeJS.Timeout>()
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const resolveText = (spanish?: string, english?: string, fallback?: string) => {
    return spanish || labelEs || english || labelEn || fallback || ''
  }
  
  const displayLabel = resolveText(labelEs, labelEn, label)
  const displayLoadingText = resolveText(
    loadingTextEs || 'Procesando...',
    loadingTextEn || 'Processing...',
    loadingText
  )
  const displaySuccessText = resolveText(
    successTextEs || 'Completado',
    successTextEn || 'Completed',
    successText
  )
  const displayDescription = resolveText(describedByEs, describedByEn, describedBy)
  
  // BIRHAUS Principle #9: Consistency via tokens
  const baseClasses = `
    inline-flex items-center justify-center gap-2 rounded-lg font-medium 
    transition-all duration-200 ease-in-out
    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
    disabled:pointer-events-none disabled:opacity-50
    ${fullWidth ? 'w-full' : ''}
  `.trim()
  
  const sizeClasses = {
    xs: 'h-7 px-2 text-xs',
    sm: 'h-8 px-3 text-sm',
    md: 'h-10 px-4 text-sm',
    lg: 'h-11 px-6 text-base',
    xl: 'h-12 px-8 text-base'
  }
  
  // BIRHAUS Principle #3: One clear action (primary action gets special treatment)
  const variantClasses = {
    primary: isPrimaryAction 
      ? 'bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500 shadow-md hover:shadow-lg'
      : 'bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500',
    secondary: 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 focus-visible:ring-gray-500',
    destructive: 'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500',
    outline: 'border border-gray-300 bg-transparent text-gray-700 hover:bg-gray-50 focus-visible:ring-gray-500',
    ghost: 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus-visible:ring-gray-500'
  }
  
  // BIRHAUS: Cognitive load visual indicators
  const cognitiveLoadIndicator = trackCognitiveLoad ? safeAccess({
    low: 'ring-1 ring-green-300',
    medium: 'ring-1 ring-yellow-300', 
    high: 'ring-1 ring-red-300'
  }, cognitiveLoadLevel, '') : ''
  
  // BIRHAUS Principle #5: Undo over confirm - Handle destructive actions
  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (destructive && undoAction && !showUndoPrompt) {
      // Execute destructive action immediately, then show undo option
      await onClick?.(event)
      
      setShowUndoPrompt(true)
      
      // Set timeout to hide undo option
      undoTimeoutRef.current = setTimeout(() => {
        setShowUndoPrompt(false)
      }, undoTimeoutMs)
      
      return
    }
    
    // Regular action
    const result = onClick?.(event)
    
    // Show success state if configured
    if (showSuccessState && result && typeof result === 'object' && 'then' in result) {
      try {
        await result
        setShowSuccess(true)
        setTimeout(() => setShowSuccess(false), 2000)
      } catch (error) {
        // Handle error state
        console.error('Action failed:', error)
      }
    }
  }
  
  // Handle undo action
  const handleUndo = async () => {
    if (!undoAction) return
    
    setIsUndoing(true)
    
    try {
      await undoAction()
      setShowUndoPrompt(false)
      
      // Clear timeout
      if (undoTimeoutRef.current) {
        clearTimeout(undoTimeoutRef.current)
      }
    } catch (error) {
      console.error('Undo failed:', error)
    } finally {
      setIsUndoing(false)
    }
  }
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (undoTimeoutRef.current) {
        clearTimeout(undoTimeoutRef.current)
      }
    }
  }, [])
  
  // BIRHAUS: Show undo prompt instead of button when destructive action executed
  if (showUndoPrompt) {
    return (
      <div className="inline-flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg">
        <AlertTriangle className="h-4 w-4 text-red-600" />
        <span className="text-sm text-red-800">Acción completada</span>
        <button
          onClick={handleUndo}
          disabled={isUndoing}
          className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-red-700 bg-white border border-red-300 rounded hover:bg-red-50 disabled:opacity-50"
        >
          <Undo2 className="h-3 w-3" />
          {isUndoing ? 'Deshaciendo...' : 'Deshacer'}
        </button>
      </div>
    )
  }
  
  return (
    <button
      ref={ref}
      className={cn(
        baseClasses,
        safeAccess(sizeClasses, size, sizeClasses.md),
        safeAccess(variantClasses, variant, variantClasses.primary),
        cognitiveLoadIndicator,
        className
      )}
      disabled={disabled || loading}
      onClick={handleClick}
      aria-describedby={displayDescription ? `${props.id}-desc` : undefined}
      {...props}
    >
      {/* Loading state */}
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      
      {/* Success state */}
      {showSuccess && <Check className="h-4 w-4 text-green-600" />}
      
      {/* Icon */}
      {icon && iconPosition === 'left' && !loading && !showSuccess && (
        <span className="flex-shrink-0">{icon}</span>
      )}
      
      {/* Button content */}
      <span>
        {loading 
          ? displayLoadingText
          : showSuccess 
            ? displaySuccessText
            : children || displayLabel
        }
      </span>
      
      {/* Icon right */}
      {icon && iconPosition === 'right' && !loading && !showSuccess && (
        <span className="flex-shrink-0">{icon}</span>
      )}
      
      {/* Accessibility description */}
      {displayDescription && (
        <span id={`${props.id}-desc`} className="sr-only">
          {displayDescription}
        </span>
      )}
    </button>
  )
})

BirhausButton.displayName = 'BirhausButton'

// BIRHAUS: Hook for managing button state
export function useBirhausButton() {
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  
  const executeAction = async (action: () => Promise<void>) => {
    setLoading(true)
    try {
      await action()
      setSuccess(true)
      setTimeout(() => setSuccess(false), 2000)
    } catch (error) {
      console.error('Button action failed:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }
  
  return {
    loading,
    success,
    executeAction,
    setLoading,
    setSuccess
  }
}

// Component and hook are exported via individual export declarations above